import { formatUtcDay } from '@constants/utcDay';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { type SeatUsageDay } from '@store/api/seatUsageEndpoints';
import { red } from '@theme/primitives';
import { useId } from 'react';

/**
 * One application's seat usage, one column per UTC day.
 *
 * Two bands on one time axis, because they count different things. The upper one is devices at
 * once: the peak of seats held, the seat limit as a line, and above the line whatever went beyond
 * it, seats taken in a race and devices working offline while every seat was taken. The lower one
 * is offline time, split the same way.
 *
 * **Beyond the limit is never colour alone** (the spec's rule): it is hatched as well, a marker
 * stands above every day that exceeded the limit, the legend names both, and each day's tooltip
 * says it in words. Inline SVG like the Activity sparkline, and for the same reason: rectangles, a
 * stepped line and a pattern are the whole requirement.
 */

const WIDTH = 1000;
/** Heights in pixels. The SVG is drawn at exactly this height, so only its width is scaled. */
const SEATS = 140;
const GAP = 16;
const OFFLINE = 56;
const HEIGHT = SEATS + GAP + OFFLINE;
const MARKERS = 18;
const GUTTER = 88;

/** A day in words, for its tooltip. */
const describe = (day: SeatUsageDay) => [
  `${formatUtcDay(day.date)} (UTC)`,
  `${day.peakSeatsInUse} of ${day.seatLimit.formatCount('seat', 'seats')} in use at the peak`,
  day.peakBeyondLimit > 0 &&
    `${day.peakBeyondLimit.formatCount('device', 'devices')} offline at once while every seat was taken`,
  day.refusedRequests > 0 && `${day.refusedRequests.formatCount('request', 'requests')} refused, no seat free`,
  day.offlineSeconds > 0 && `${day.offlineSeconds.formatDuration()} offline` +
    (day.offlineBeyondLimitSeconds > 0 ? `, ${day.offlineBeyondLimitSeconds.formatDuration()} of it beyond the limit` : ''),
  // The server's verdict on the day, in the words of the figures above the chart.
  day.exceeded ? 'Seat limit exceeded' : day.atLimit && 'Seat limit reached'
].filter(Boolean).join('\n');

const Swatch = ({ sx }: { sx: object }) => <Box sx={{ width: 12, height: 12, borderRadius: 0.5, flexShrink: 0, ...sx }} />;

const SeatUsageChart = ({ days, label }: { days: SeatUsageDay[], label: string }) => {
  const theme = useTheme();
  const hatchId = `beyond${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  const online = theme.palette.primary.main;
  const offline = theme.palette.secondary.main;
  const beyond = theme.palette.error.light;
  const hatchCss = {
    backgroundColor: beyond,
    backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 3px, rgba(255, 255, 255, 0.45) 3px 5px)'
  };

  // A column reaches the day's peak usage as the server counts it (`dayPeakUsage`), so the chart and
  // the summary above it never disagree: with devices offline while every seat was taken, the whole
  // limit plus those devices, since the pool was full while they ran, or the seats held when more.
  // Whatever goes past the limit is drawn above it.
  const reach = (day: SeatUsageDay) => day.peakBeyondLimit > 0
    ? Math.max(day.peakSeatsInUse, day.seatLimit + day.peakBeyondLimit)
    : day.peakSeatsInUse;

  // The limit line has to fit as well as the columns.
  const seatsMax = Math.max(1, ...days.map(day => Math.max(reach(day), day.seatLimit)));
  const offlineMax = Math.max(0, ...days.map(day => day.offlineSeconds));

  const seatY = (value: number) => SEATS - (value / seatsMax) * (SEATS - 4);
  const offlineY = (seconds: number) => HEIGHT - (seconds / Math.max(offlineMax, 1)) * (OFFLINE - 2);

  const slot = WIDTH / days.length;
  // Gaps between columns while they are wide enough to show; a year or more is drawn solid.
  const bar = days.length > 120 ? slot : slot * 0.7;
  const x = (index: number) => index * slot + (slot - bar) / 2;

  const limitLine = days.map((day, index) =>
    `${index ? 'L' : 'M'}${(index * slot).toFixed(2)},${seatY(day.seatLimit).toFixed(2)} H${((index + 1) * slot).toFixed(2)}`
  ).join(' ');

  /** A bar segment from `bottom` up to `top`, in SVG coordinates, so `top` is the smaller. */
  const rect = (key: string, index: number, bottom: number, top: number, fill: string) => bottom - top > 0.01 && (
    <rect key={key} x={x(index)} y={top} width={bar} height={bottom - top} fill={fill} />
  );

  const first = formatUtcDay(days[0].date);
  const last = formatUtcDay(days[days.length - 1].date);

  return (
    <Box>
      <Stack direction='row' spacing={2.5} sx={{ flexWrap: 'wrap', rowGap: 0.5, mb: 1.5 }}>
        {[
          { key: 'online', swatch: <Swatch sx={{ bgcolor: online }} />, text: 'Peak seats in use' },
          {
            key: 'limit',
            swatch: <Box sx={{ width: 16, borderTop: `2px dashed ${theme.palette.text.primary}` }} />,
            text: 'Seat limit'
          },
          { key: 'beyond', swatch: <Swatch sx={hatchCss} />, text: 'Beyond the limit' },
          { key: 'offline', swatch: <Swatch sx={{ bgcolor: offline }} />, text: 'Offline use within the limit' },
          {
            key: 'exceeded',
            swatch: <Typography variant='caption' sx={{ color: red[300], lineHeight: 1 }}>▲</Typography>,
            text: 'Limit exceeded that day'
          }
        ].map(item => (
          <Stack key={item.key} direction='row' spacing={0.75} alignItems='center'>
            {item.swatch}
            <Typography variant='caption' color='text.secondary'>{item.text}</Typography>
          </Stack>
        ))}
      </Stack>

      <Box sx={{ display: 'flex' }}>
        {/* A fixed gutter for the scale, so the plot starts at the same x whatever the numbers. */}
        <Box sx={{ position: 'relative', width: GUTTER, flexShrink: 0 }}>
          <Typography
            variant='caption' color='text.secondary'
            sx={{ position: 'absolute', top: MARKERS - 2, right: 8, whiteSpace: 'nowrap' }}
          >
            {seatsMax.formatCount('seat', 'seats')}
          </Typography>
          <Typography
            variant='caption' color='text.secondary'
            sx={{ position: 'absolute', top: MARKERS + SEATS + GAP - 2, right: 8, textAlign: 'right', lineHeight: 1.3 }}
          >
            {offlineMax > 0 ? offlineMax.formatDuration() : 'none'}<br />offline
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box sx={{ position: 'relative', height: MARKERS }}>
            {days.map((day, index) => day.exceeded && (
              <Typography
                key={day.date}
                variant='caption'
                aria-hidden
                sx={{
                  position: 'absolute', left: `${((index + 0.5) / days.length) * 100}%`, bottom: 0,
                  transform: 'translateX(-50%)', color: red[300], lineHeight: 1
                }}
              >
                ▲
              </Typography>
            ))}
          </Box>

          <Box
            component='svg'
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio='none'
            sx={{ width: '100%', height: HEIGHT, display: 'block' }}
            role='img'
            aria-label={`${label}: peak seats in use against the seat limit, and offline time, per day from ${first} to ${last}, in UTC`}
          >
            <defs>
              <pattern id={hatchId} width={6} height={6} patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
                <rect width={6} height={6} fill={beyond} />
                <rect width={2.5} height={6} fill={theme.palette.common.white} fillOpacity={0.45} />
              </pattern>
            </defs>

            <line x1={0} x2={WIDTH} y1={SEATS} y2={SEATS} stroke={theme.palette.divider} vectorEffect='non-scaling-stroke' />
            <line x1={0} x2={WIDTH} y1={HEIGHT} y2={HEIGHT} stroke={theme.palette.divider} vectorEffect='non-scaling-stroke' />

            {days.map((day, index) => [
              rect(`${day.date}-online`, index, SEATS, seatY(Math.min(day.peakSeatsInUse, day.seatLimit)), online),
              reach(day) > day.seatLimit &&
                rect(`${day.date}-beyond`, index, seatY(day.seatLimit), seatY(reach(day)), `url(#${hatchId})`),
              rect(`${day.date}-offline`, index, HEIGHT, offlineY(day.offlineSeconds - day.offlineBeyondLimitSeconds), offline),
              rect(`${day.date}-offline-beyond`, index,
                offlineY(day.offlineSeconds - day.offlineBeyondLimitSeconds), offlineY(day.offlineSeconds), `url(#${hatchId})`)
            ])}

            <path
              d={limitLine} fill='none' stroke={theme.palette.text.primary} strokeWidth={1.5}
              strokeDasharray='6 4' vectorEffect='non-scaling-stroke'
            />

            {/* One column per day across both bands, for the tooltip. */}
            {days.map((day, index) => (
              <rect key={`${day.date}-hover`} x={index * slot} y={0} width={slot} height={HEIGHT} fill='transparent'>
                <title>{describe(day)}</title>
              </rect>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant='caption' color='text.secondary'>{first}</Typography>
            <Typography variant='caption' color='text.secondary'>Days in UTC</Typography>
            <Typography variant='caption' color='text.secondary'>{last}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SeatUsageChart;
